import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d99s4uag3iubr452ip5g';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* use empty object */ }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || 'Submission failed. Please try again.';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Network error. Please try again.');
      setFormStatus('error');
    }
  };

  return (
    <footer className="w-full bg-accent-50 text-accent-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <p className="font-heading text-2xl font-bold text-accent-900 mb-3">Superbuy Sheet</p>
            <p className="text-accent-700 text-sm leading-relaxed">
              Your central hub for organized Superbuy Spreadsheet resources. Browse curated product categories, access verified shopping links, and discover the latest finds — all in one place.
            </p>
          </div>

          <div>
            <h4 className="font-label text-xs font-semibold uppercase tracking-wider text-accent-600 mb-4">Newsletter</h4>
            <form onSubmit={handleSubmit} data-readdy-form="">
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  className="w-full bg-transparent border-0 border-b border-accent-400 pb-2 text-accent-900 text-sm placeholder:text-accent-400 focus:outline-none focus:border-accent-700 transition-colors duration-200"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-0 text-accent-700 cursor-pointer hover:text-accent-900 transition-colors duration-150"
                  aria-label="Subscribe"
                >
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </div>
              {formStatus === 'success' && (
                <p className="text-accent-600 text-xs mt-2">Subscribed successfully!</p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-500 text-xs mt-2">{formError}</p>
              )}
            </form>
            <p className="text-accent-500 text-xs mt-3">
              Get the latest Superbuy spreadsheet updates.
            </p>
          </div>

          <div>
            <h4 className="font-label text-xs font-semibold uppercase tracking-wider text-accent-600 mb-4">Resources</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/superbuy-spreadsheet-guide" className="text-accent-700 text-sm hover:text-accent-900 transition-colors duration-150 cursor-pointer">
                  Complete Guide
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-700 text-sm hover:text-accent-900 transition-colors duration-150 cursor-pointer">
                  All Categories
                </a>
              </li>
              <li>
                <Link to="/" className="text-accent-700 text-sm hover:text-accent-900 transition-colors duration-150 cursor-pointer">
                  Home Hub
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-label text-xs font-semibold uppercase tracking-wider text-accent-600 mb-4">Social</h4>
            <ul className="space-y-2.5">
              <li>
                <a href="https://pinterest.com" target="_blank" rel="noopener nofollow" className="text-accent-700 text-sm hover:text-accent-900 transition-colors duration-150 cursor-pointer">
                  Pinterest
                </a>
              </li>
              <li>
                <a href="https://reddit.com" target="_blank" rel="noopener nofollow" className="text-accent-700 text-sm hover:text-accent-900 transition-colors duration-150 cursor-pointer">
                  Reddit
                </a>
              </li>
              <li>
                <a href="https://quora.com" target="_blank" rel="noopener nofollow" className="text-accent-700 text-sm hover:text-accent-900 transition-colors duration-150 cursor-pointer">
                  Quora
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full bg-accent-100 py-8 px-4 md:px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <p className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-accent-300 tracking-tight whitespace-nowrap select-none">
            SUPERBUY
          </p>
        </div>
      </div>

      <div className="w-full bg-accent-200 py-6 px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-accent-700 text-xs">
            &copy; {new Date().getFullYear()} Superbuy Spreadsheet Hub. All rights reserved.
          </p>
          <p className="text-accent-500 text-xs">
            External links open on kaspreadsheet.com
          </p>
        </div>
      </div>
    </footer>
  );
}